import React, { Suspense } from 'react';
import { Outlet, Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@auth/presentation/hooks/useAuth';
import { LoadingSpinner } from '@klonup/design-system';
import { useOrganizations } from '@organizations/presentation/hooks/useOrganizations';
import { useOrgDetail } from '@organizations/presentation/hooks/useOrgDetail';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import ProfileDropdown from '@shared/components/ProfileDropdown';
import logotipo from '@klonup/logos/RGB - Logotipo Klonup.svg';

const AdminHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();

  const navLinks = [
    { to: '/admin/organizations', label: t('nav.organizations') },
  ];

  // Identidad de la organización visitada — mismo patrón que OrgHeader (org-header-identity),
  // pero derivado del slug de la ruta en vez de "mi organización": el SuperAdmin navega entre
  // varias orgs, así que solo se resuelve cuando la ruta es un detalle concreto
  // (/admin/organizations/:slug). En la lista (sin :slug) `organization` queda null y no se
  // muestra nada — igual que en la lista no hay una org "actual" que mostrar en el header.
  const { organizations } = useOrganizations();
  const stateOrgId = (location.state as { orgId?: string } | null)?.orgId;
  const orgId = slug ? (stateOrgId ?? organizations.find(o => o.slug === slug)?.id) : undefined;
  const { organization } = useOrgDetail(orgId);

  return (
    <header style={{ height: '72px', backgroundColor: 'white', borderBottom: '1px solid #F3F4F6', position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

        <div className="flex items-center gap-10">
          <Link to="/admin/organizations" className="flex items-center">
            <img src={logotipo} alt="Klonup" style={{ height: '54px', width: 'auto' }} />
          </Link>

          <nav className="flex items-center gap-8">
            {navLinks.map(({ to, label }) => {
              const isActive = location.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={isActive ? 'text-klonup-indigo' : 'text-gray-500'}
                  style={{
                    position: 'relative',
                    fontWeight: isActive ? 800 : 700,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    padding: '1.5rem 0.5rem',
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--color-klonup-indigo)'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = '#6B7280'; }}
                >
                  {label}
                  {isActive && (
                    <span style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '3px',
                      backgroundColor: 'var(--color-klonup-sky)',
                      borderRadius: '3px 3px 0 0',
                    }} />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Va DESPUÉS del nav a propósito: si se insertase entre el logo y "ORGANIZACIONES",
              entrar en el detalle de una org empujaría el link de sitio. Aquí el nav queda
              siempre pegado al logo y la identidad de la org solo se añade a su derecha. */}
          {organization !== null && (
            <div className="flex items-center gap-6">
              <div aria-hidden="true" style={{ width: '1px', height: '32px', backgroundColor: '#E5E7EB' }} />
              <div>
                <p style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '15px', color: 'var(--color-klonup-indigo)', margin: 0, lineHeight: 1.3 }}>
                  {organization.name}
                </p>
                <p style={{ fontSize: '12px', color: '#94A3B8', margin: 0, lineHeight: 1.3 }}>
                  {t('admin.org_detail.plan', { plan: organization.planName })}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-6">
          <ProfileDropdown user={user} logout={logout} settingsPath="/admin/settings" />
        </div>

      </div>
    </header>
  );
};

const AdminLayout: React.FC = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAFC' }}>
      <AdminHeader />
      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2.5rem 2rem', overflowX: 'hidden' }}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner size="md" className="h-96" />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
};

export default AdminLayout;
